import {
  useState, useCallback, useMemo, cloneElement, ReactElement,
} from 'react';
import {
  ToggleButton as MUIToggleButton,
  ToggleButtonProps as MUIToggleButtonProps,
} from '@mui/material';
import {
  B1, AB3, S1, S2, S4,
} from '../../theme';
import { mergeSX } from '../../services/helpers';

export enum ToggleButtonVariantEnum {
  Square = 'square',
  Rounded = 'rounded',
}

export interface IToggleButtonProps extends Omit<MUIToggleButtonProps, 'value'> {
  icon: ReactElement;
  value?: string;
  variant?: ToggleButtonVariantEnum.Rounded | ToggleButtonVariantEnum.Square;
  defaultIconColor?: string;
  defaultButtonBackground?: string;
  hoveredIconColor?: string;
  hoveredButtonBackground?: string;
  selectedIconColor?: string;
  selectedButtonBackground?: string;
  selectedAndHoveredIconColor?: string;
  selectedAndHoveredButtonBackground?: string;
  minWidth?: number;
  minHeight?: number;
}

interface IBuildSXParams {
  variant: ToggleButtonVariantEnum;
  backgroundColor: string;
  minWidth: number;
  minHeight: number;
}

const buildSX = ({
  variant, backgroundColor, minWidth, minHeight,
}: IBuildSXParams) => ({
  '&.MuiToggleButton-root': {
    backgroundColor,
    '&:hover': {
      backgroundColor,
    },
    border: 'none',
    borderRadius: variant === ToggleButtonVariantEnum.Rounded ? '50%' : undefined,
    '&.MuiToggleButton-sizeSmall': {
      minWidth,
      minHeight,
    },
  },
});

const ToggleButton = (props: IToggleButtonProps) => {
  const {
    icon,
    variant = ToggleButtonVariantEnum.Rounded,
    defaultIconColor, defaultButtonBackground,
    hoveredIconColor, hoveredButtonBackground,
    selectedIconColor, selectedButtonBackground,
    selectedAndHoveredIconColor, selectedAndHoveredButtonBackground,
    value = 'toggleButton',
    size = 'small',
    minWidth = 48,
    minHeight = 48,
    selected,
    onMouseEnter,
    onMouseLeave,
    sx,
    ...restProps
  } = props;

  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = useCallback<
    NonNullable<IToggleButtonProps['onMouseEnter']>
  >((event) => {
    setIsHovered(true);
    if (onMouseEnter) {
      onMouseEnter(event);
    }
  }, [onMouseEnter]);

  const handleMouseLeave = useCallback<
    NonNullable<IToggleButtonProps['onMouseLeave']>
  >((event) => {
    setIsHovered(false);
    if (onMouseLeave) {
      onMouseLeave(event);
    }
  }, [onMouseLeave]);

  const colors = useMemo(() => ({
    'false-false': {
      color: defaultIconColor ?? S4,
      backgroundColor: defaultButtonBackground ?? S2,
    },
    'false-true': {
      color: hoveredIconColor ?? AB3,
      backgroundColor: hoveredButtonBackground ?? S2,
    },
    'true-false': {
      color: selectedIconColor ?? S1,
      backgroundColor: selectedButtonBackground ?? AB3,
    },
    'true-true': {
      color: selectedAndHoveredIconColor ?? S1,
      backgroundColor: selectedAndHoveredButtonBackground ?? B1,
    },
  }), [
    defaultIconColor, defaultButtonBackground,
    hoveredIconColor, hoveredButtonBackground,
    selectedIconColor, selectedButtonBackground,
    selectedAndHoveredIconColor, selectedAndHoveredButtonBackground,
  ]);

  const { color, backgroundColor } = colors[`${!!selected}-${isHovered}`];

  const iconWithProps = cloneElement(icon, { color });

  return (
    <MUIToggleButton
      value={value}
      size={size}
      selected={selected}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={mergeSX(buildSX({
        variant, backgroundColor, minWidth, minHeight,
      }), sx)}
      {...restProps}
    >
      {iconWithProps}
    </MUIToggleButton>
  );
};

export default ToggleButton;
